import { Button, Card, Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import HtmlDefault from "common/fragments/HtmlDefault";
import HeadingView from "common/views/heading/Heading.view";
import { HeadingVariant } from "common/views/heading/Heading.view";
import { Fragment } from "core/components/FragmentProvider";
import DateLabel from "core/components/label/DateLabel";
import Label from "core/components/label/Label";
import React from "react";
import { ImageView } from "../../../../common/views/image/Image.view";
import { ITopicFragmentProps } from "./TopicComponent";
import TopicDisplayMode from "./TopicDisplayMode";
import VerticalCenter from "../../../../common/fragments/VerticalCenter";
import styled from "styled-components";

export const CreatedDate: Fragment<ITopicFragmentProps> = {
	name: "Posted date",
	enabled: ({ topic }) => topic.createdOn === topic.modifiedOn,
	component: ({ config, topic }) =>
		(
			<React.Fragment>
				{config.metaIcons && <Icon icon={IconNames.CALENDAR} />}

				{!config.minimal && (
					<Label k="topic.createdDate" v="Posted: " />
				)}
				<DateLabel value={topic.createdOn} />
			</React.Fragment>
		)
};

export const ModifiedDate: Fragment<ITopicFragmentProps> = {
	name: "Edited date",
	enabled: ({ topic }) => topic.createdOn !== topic.modifiedOn,
	component: ({ config, topic }) => (
		<React.Fragment>
			{config.metaIcons && <Icon icon={IconNames.CALENDAR} />}

			{!config.minimal && (
				<Label k="topic.modifyDate" v="Edited: " />
			)}

			<DateLabel value={topic.modifiedOn} />
		</React.Fragment>
	)
};

export const Views: Fragment<ITopicFragmentProps> = {
	name: "View count",
	enabled: ({ topic }) => topic.stats != null,
	component: ({ config, topic }) => (
		<React.Fragment>
			{config.metaIcons && <Icon icon={IconNames.EYE_OPEN} />}

			{!config.minimal && (
				<Label k="topic.views" v="Views" />
			)}

			{topic.stats.views}
		</React.Fragment>
	)
};

export const Upvotes: Fragment<ITopicFragmentProps> = {
	name: "Upvotes",
	enabled: ({ topic }) => topic.stats != null,
	component: ({ config, topic }) => (
		<React.Fragment>
			{config.metaIcons && <Icon icon={IconNames.ARROW_UP} />}

			{!config.minimal && (
				<Label k="topic.upvotes" v="Upvotes" />
			)}

			{topic.stats.votePositive}
		</React.Fragment>
	)
};

export const Downvotes: Fragment<ITopicFragmentProps> = {
	name: "Downvotes",
	enabled: ({ topic }) => topic.stats != null,
	component: ({ config, topic }) => (
		<React.Fragment>
			{config.metaIcons && <Icon icon={IconNames.ARROW_DOWN} />}

			{!config.minimal && (
				<Label k="topic.downvotes" v="Downvotes" />
			)}

			{topic.stats.voteNegative}
		</React.Fragment>
	)
};

export const Replies: Fragment<ITopicFragmentProps> = {
	name: "Replies",
	enabled: ({ topic, mode }) => TopicDisplayMode.OVERVIEW === mode && topic.stats != null,
	component: ({ config, topic, actions }) => (
		<a onClick={() => actions.navigateTo()}>
			{config.metaIcons && <Icon icon={IconNames.COMMENT} />}

			{!config.minimal && (
				<Label k="topic.replies" v="Replies" />
			)}

			{topic.stats.comments}
		</a>
	)
};

export const AuthorAvatar: Fragment<ITopicFragmentProps> = {
	name: "Author avatar",
	enabled: ({ topic }) => topic.createdBy != null,
	component: ({ topic }) => {

		if (topic.createdBy.avatar && topic.createdBy.avatar.file != null) {
			return <ImageView src={topic.createdBy.avatar.file.url} viewKey="topic.avatar" />;
		}

		return <Icon icon={IconNames.USER} iconSize={80} />;
	}
};

export const AuthorName: Fragment<ITopicFragmentProps> = {
	name: "Author name",
	enabled: ({ topic }) => topic.createdBy != null,
	component: ({ topic }) => (
		<span>{topic.createdBy.firstName} {topic.createdBy.lastName}</span>
	)
};

export const Body: Fragment<ITopicFragmentProps> = {
	name: "Body",
	enabled: ({ topic }) => topic.description != null,
	component: ({ topic }) => (
		<HtmlDefault html={topic.description} />
	)
};

export const Title: Fragment<ITopicFragmentProps> = {
	name: "Title",
	enabled: ({ topic }) => topic.name != null,
	component: ({ topic }) => (
		<span>{topic.name}</span>
	)
};

export const HeaderTitle: Fragment<ITopicFragmentProps> = {
	name: "Header Title",
	enabled: ({ topic }) => topic.name != null,
	component: ({ topic, mode, actions }) => (
		<HeadingView viewKey="topic.title" variant={HeadingVariant.H2}>

			{TopicDisplayMode.OVERVIEW === mode && (
				<a onClick={() => actions.navigateTo()}>{topic.name}</a>
			)}

			{TopicDisplayMode.PARENT === mode && (
				<span>{topic.name}</span>
			)}

		</HeadingView>
	)
};

const UploadBox = styled(Card)`
	padding: 0.5em !important;
	display: flex;
	align-items: center;
	margin-top: 0.8em;

	.primary {
		flex-grow: 1;
		margin: 0px 0.8em;
	}

	.actions:first-child {
		margin-right: 0.8em;
	}

	img {
		max-height: 50px;
		max-width: 100px;
	}
`;

export const Attachments: Fragment<ITopicFragmentProps> = {
	name: "Attachments",
	enabled: ({topic}) => topic.attachments != null,
	component: ({topic}) => (
		topic.attachments.map((u) => (
			<UploadBox key={u.url}>
				{u.type && u.type.startsWith('image') && (
					<VerticalCenter >
						<img src={u.url} />
					</VerticalCenter>
				)}
				<div className="primary">
					{(u.name && (
						<a href={u.url} target="_blank">{u.name}</a>
					)) || (
						<span>{u.url}</span>
					)}
				</div>
				<div className="actions">
					<Button
						minimal={true}
						icon={IconNames.SHARE}
						onClick={() => window.open(u.url)}
					/>
				</div>
			</UploadBox>
		))
	)
};



// WEBPACK FOOTER //
// ./src/panels/forum/component/topic/topicMeta.tsx